import React from 'react'
import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { DefaultLmc } from '@lmc/ui-kit'
import { colors } from '@lmc/common/constants'
import type { ContingencyPlanDTO } from '@lmc/ts-common'
import { Link, useLocation } from 'react-router-dom'
import MultiSelectField from 'components/fields/types/MultiSelectField'
import useContingencyPlansList from 'hooks/useContingencyPlanList'
import { EditTooltip } from 'screens/Residents/screens/ResidentProfileScreen/components/EditTooltip'

type ContingencyPlanOption = {
  id: string
  name: string
}

type ContingencyPlanItem = ContingencyPlanDTO | string

export interface ContingencyPlanSelectorProps {
  selectedContingencyPlans: ContingencyPlanItem[]
  onChange: (plans: ContingencyPlanDTO[]) => void
  readonly?: boolean
  label?: React.ReactNode
  residentId?: string
}

export const ContingencyPlanSelector: React.FC<ContingencyPlanSelectorProps> = ({
  selectedContingencyPlans,
  onChange,
  readonly = false,
  label,
  residentId
}) => {
  const location = useLocation()
  const { contingencyPlans, isLoading } = useContingencyPlansList({
    residentID: residentId,
  })

  const contingencyPlanOptions: ContingencyPlanOption[] = React.useMemo(
    () =>
      (contingencyPlans || []).map((plan) => ({
        id: plan.id,
        name: plan.planName
      })),
    [contingencyPlans]
  )
  const formatContingencyPlanOption = (option: ContingencyPlanOption) => (
    <ContingencyPlanOptionWrapper>
      <IconContainer>
        <DefaultLmc />
      </IconContainer>
      <ContingencyPlanName>{option.name}</ContingencyPlanName>
    </ContingencyPlanOptionWrapper>
  )

  const normalizedSelectedPlans: ContingencyPlanDTO[] | null = React.useMemo(() => {
    if (!selectedContingencyPlans?.length) return []

    if (typeof selectedContingencyPlans[0] === 'string') {
      if (isLoading) return null

      return (selectedContingencyPlans as string[])
        .map((id) => contingencyPlans.find((plan) => plan.id === id))
        .filter((plan): plan is ContingencyPlanDTO => !!plan)
    }

    return selectedContingencyPlans as ContingencyPlanDTO[]
  }, [selectedContingencyPlans, contingencyPlans, isLoading])

  const selectedOptionValues: ContingencyPlanOption[] | null = React.useMemo(() => {
    if (normalizedSelectedPlans === null) return null

    return normalizedSelectedPlans
      .map((plan) =>
        contingencyPlanOptions.find((option) => option.id === plan.id)
      )
      .filter((option): option is ContingencyPlanOption => !!option)
  }, [normalizedSelectedPlans, contingencyPlanOptions])

  const handleChange = (selectedPlans: ContingencyPlanOption[]) => {
    const mappedPlans = selectedPlans
      .map((selectedPlan) =>
        contingencyPlans.find((plan) => plan.id === selectedPlan.id)
      )
      .filter((plan): plan is ContingencyPlanDTO => !!plan)

    onChange(mappedPlans)
  }

  if (readonly) {
    return (
      <SelectorWrapper>
        <ReadonlyWrapper>
          {label}
          {normalizedSelectedPlans === null ? (
            <Text level={1.5}>--</Text>
          ) : normalizedSelectedPlans.length > 0 ? (
            <ReadonlyIconsContainer>
              {normalizedSelectedPlans.map((plan) => (
                <EditTooltip
                  key={plan.id}
                  title={
                    <TooltipLink
                      to={`/residents/${residentId}/future-plans/${plan.id}`}
                      state={{ referrer: location.pathname }}
                    >
                      <TooltipContent>
                        <IconContainer>
                          <DefaultLmc />
                        </IconContainer>
                        {plan.planName}
                      </TooltipContent>
                    </TooltipLink>
                  }
                >
                  <TooltipWrapper>
                    <IconContainer>
                      <DefaultLmc />
                    </IconContainer>
                  </TooltipWrapper>
                </EditTooltip>
              ))}
            </ReadonlyIconsContainer>
          ) : (
            <Text level={1.5}>Not linked to any future plan</Text>
          )}
        </ReadonlyWrapper>
      </SelectorWrapper>
    )
  }

  if (selectedOptionValues === null) {
    return (
      <SelectorWrapper>
        <Text level={1.5}>--</Text>
      </SelectorWrapper>
    )
  }

  return (
    <SelectorWrapper>
      <MultiSelectField
        formKey={undefined}
        label={label}
        customOnChange={handleChange}
        onChange={() => {}}
        options={contingencyPlanOptions}
        formatOptionLabel={formatContingencyPlanOption}
        customValues={selectedOptionValues}
        centerLabel
        errors={undefined}
        style={null}
        placeholder=""
        values={[]}
      />
    </SelectorWrapper>
  )
}

const SelectorWrapper = styled.div`
  margin-top: 10px;
  background: ${colors.white};
  border-radius: 15px;
  padding: 8px 16px;
  display: flex;
`

const ReadonlyWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const ContingencyPlanOptionWrapper = styled.div`
  align-items: center;
  display: flex;
`

const ContingencyPlanName = styled.span``

const ReadonlyIconsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const TooltipWrapper = styled.div`
  display: inline-flex;
`

const TooltipContent = styled.div`
  display: flex;
  align-items: center;
`

const TooltipLink = styled(Link)`
  color: inherit;
  text-decoration: underline;
`

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 28px;
  height: 28px;
  margin-right: 5px;
  background-color: ${({ theme }) => theme.color.status.secondary};
  border-radius: 4px;
`
